// AoZweckeFeld.tsx — die steuerbegünstigten Zwecke (§§ 52–54 AO) als Liste,
// in Kontakt-Anlage UND Kontakt-Akte.
//
// SEIT 01.10.2026 (User) eine Liste im Stil der Branchen-Auswahl statt der
// 29 Zeilen langen Ankreuzliste (Akte) bzw. der kompakten Mehrfachauswahl
// (Anlage, deren Komma-Liste im geschlossenen Feld die Zeile sprengte): je
// gewähltem Zweck eine Zeile mit Fundstelle und „Entfernen", darunter
// „+ Zweck hinzufügen" als Such-Dropdown, das nur noch nicht gewählte Zwecke
// anbietet. Eine Satzung nennt regelmäßig zwei bis drei Zwecke („Sport" +
// „traditionelles Brauchtum") — die Liste zeigt genau die, nicht 29.
//
// Gespeichert wird unverändert die CSV der stabilen `code`-Werte
// (`Contact.zweckNrAO`), in der Reihenfolge des Gesetzes; Serialisierung,
// Parsing und Ordnung kommen aus `data/aoZwecke.ts` und werden hier NICHT
// nachgebaut. Codes, die der Katalog nicht (mehr) kennt, bleiben als eigene
// Zeile stehen, statt beim nächsten Speichern unbemerkt aus dem Datensatz zu
// fallen.

import * as React from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { REFINED } from "../risikoanalyseStep/tokens";
import { SearchableSelect, type SelectOption } from "../forms/widgets/SearchableSelect";
import { FeldPraesenz } from "./FeldPraesenz";
import {
  AO_ZWECKE,
  aoZweckLabel,
  parseZweckNrAO,
  zweckEntfernen,
  zweckHinzufuegen,
} from "../../data/aoZwecke";
import { elementMitId } from "../windowManager/browserFenster/fensterRegister";

const useStyles = makeStyles({
  huelle: { position: "relative", display: "flex", flexDirection: "column", rowGap: "8px" },
  label: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.78rem",
    fontWeight: 600,
    color: REFINED.ink2,
    letterSpacing: "0.02em",
  },
  zeilen: {
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
    listStyleType: "none",
    ...shorthands.margin("0"),
    ...shorthands.padding("0"),
  },
  zeile: {
    display: "flex",
    alignItems: "baseline",
    columnGap: "10px",
    flexWrap: "wrap",
    ...shorthands.padding("8px", "10px"),
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("8px"),
  },
  zweck: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.86rem",
    color: REFINED.ink,
    fontWeight: 600,
    minWidth: 0,
  },
  norm: {
    fontFamily: REFINED.fontUI,
    color: REFINED.ink3,
    fontSize: "0.78rem",
    whiteSpace: "nowrap",
  },
  hinweis: { fontFamily: REFINED.fontUI, color: REFINED.ink3, fontSize: "0.82rem" },
  linkBtn: {
    appearance: "none",
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    color: REFINED.aub,
    cursor: "pointer",
    fontFamily: REFINED.fontUI,
    fontSize: "0.78rem",
    fontWeight: 600,
    ...shorthands.padding("0"),
    alignSelf: "flex-start",
    ":hover": { textDecorationLine: "underline" },
  },
  entfernen: { marginLeft: "auto", alignSelf: "center" },
});

type AoZweckeFeldProps = {
  label: string;
  /** CSV aus `Contact.zweckNrAO`. */
  value?: string | null;
  /** Fehlt der Handler, ist das Feld nur lesbar — wie überall in der Akte. */
  onChange?: (csv: string | null) => void;
  /** Live-Präsenz: Betreten/Verlassen melden (useContactPresence). */
  onFocus?: () => void;
  onBlur?: () => void;
  /** Anzeigename des Kollegen, der gerade an diesem Feld arbeitet. */
  lockedBy?: string;
  /** Klasse der Hülle — in der Akte `.fld.span2` (volle Breite im Raster). */
  className?: string;
  /** Präfix der DOM-Id des Such-Dropdowns (mehrere Instanzen je Seite). */
  idBase?: string;
};

/** Fundstelle zum Code; ein unbekannter Code sagt es offen. */
const normVon = (code: string): string =>
  AO_ZWECKE.find((z) => z.code === code)?.norm ?? "nicht im Katalog";

export function AoZweckeFeld({
  label,
  value,
  onChange,
  onFocus,
  onBlur,
  lockedBy,
  className = "fld span2",
  idBase = "ao-zweck",
}: AoZweckeFeldProps) {
  const s = useStyles();
  const gewaehlt = React.useMemo(() => parseZweckNrAO(value), [value]);
  const [suche, setSuche] = React.useState(false);
  const suchId = `${idBase}-hinzufuegen`;

  // Nur noch nicht gewählte Zwecke anbieten — ein zweites „Sport" wäre eine
  // Dublette, die die Serialisierung ohnehin wieder verwürfe.
  const angebot: SelectOption[] = React.useMemo(() => {
    const schon = new Set(gewaehlt);
    return AO_ZWECKE.filter((z) => !schon.has(z.code)).map((z) => ({
      value: z.code,
      label: z.label,
      hint: z.norm,
    }));
  }, [gewaehlt]);

  // Das Such-Dropdown klappt beim Fokus auf: Nach dem Klick auf
  // „+ Zweck hinzufügen" kann man sofort tippen.
  React.useEffect(() => {
    if (suche) elementMitId(suchId)?.focus();
  }, [suche, suchId]);

  return (
    <div className={className} onFocusCapture={onFocus} onBlurCapture={onBlur}>
      <div className={s.huelle}>
        <span className={s.label}>{label}</span>
        {gewaehlt.length === 0 ? (
          <span className={s.hinweis}>Keine Zwecke erfasst.</span>
        ) : (
          <ul className={s.zeilen} aria-label={label}>
            {gewaehlt.map((code) => (
              <li key={code} className={s.zeile}>
                <span className={s.zweck}>{aoZweckLabel(code)}</span>
                <span className={s.norm}>{normVon(code)}</span>
                {onChange && (
                  <button
                    type="button"
                    className={mergeClasses(s.linkBtn, s.entfernen)}
                    aria-label={`${aoZweckLabel(code)} entfernen`}
                    onClick={() => onChange(zweckEntfernen(value, code))}
                  >
                    Entfernen
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {onChange &&
          angebot.length > 0 &&
          (suche ? (
            <div
              onBlur={(e) => {
                // Verlässt der Fokus das Dropdown ganz, steht wieder der Link
                // da. Verzögert, weil das Dropdown selbst erst nach seinem
                // eigenen Blur-Timer zuklappt.
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                  window.setTimeout(() => setSuche(false), 160);
                }
              }}
            >
              <SearchableSelect
                label="Zweck hinzufügen"
                name={suchId}
                value=""
                onChange={(code) => {
                  if (!code) return;
                  onChange(zweckHinzufuegen(value, code));
                  setSuche(false);
                }}
                options={angebot}
                allowFreeText={false}
                placeholder="Zweck suchen …"
                emptyText="Kein Zweck gefunden"
              />
            </div>
          ) : (
            <button type="button" className={s.linkBtn} onClick={() => setSuche(true)}>
              + Zweck hinzufügen
            </button>
          ))}
        <FeldPraesenz lockedBy={lockedBy} oben />
      </div>
    </div>
  );
}
